import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Container, 
  Typography, 
  Box, 
  Grid, 
  TextField, 
  Button, 
  Paper,
  Alert,
  Snackbar
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

export default function Contact() {
  const navigate = useNavigate();
  useEffect(() => {
    window.handleSearch = (searchValue) => {
      if (searchValue && searchValue.trim()) {
        navigate(`/listings?search=${encodeURIComponent(searchValue.trim())}`);
      }
    };
    return () => { window.handleSearch = undefined; };
  }, [navigate]);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      setSnackbar({
        open: true,
        message: 'Please fill in all fields',
        severity: 'error',
      });
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setSnackbar({
        open: true,
        message: 'Please enter a valid email address',
        severity: 'error',
      });
      return;
    }

    // Simulate form submission
    console.log('Form submitted:', formData);
    
    setSnackbar({
      open: true,
      message: 'Thank you for your message! We\'ll get back to you soon.',
      severity: 'success',
    });

    // Reset form
    setFormData({
      name: '',
      email: '',
      subject: '',
      message: '',
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const contactInfo = [
    {
      icon: <EmailIcon sx={{ fontSize: 40, color: '#6B89FF' }} />,
      title: 'Email Us',
      content: 'support@bookloop.com',
      description: 'We typically respond within 24 hours',
    },
    {
      icon: <PhoneIcon sx={{ fontSize: 40, color: '#6B89FF' }} />,
      title: 'Call Us',
      content: '+1 (555) 123-4567',
      description: 'Monday - Friday, 9AM - 6PM EST',
    },
    {
      icon: <AccessTimeIcon sx={{ fontSize: 40, color: '#6B89FF' }} />,
      title: 'Business Hours',
      content: 'Monday - Friday',
      description: '9:00 AM - 6:00 PM EST',
    },
  ];

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#E6F0F9', pt: 8 }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography variant="h2" component="h1" sx={{ 
            fontWeight: 'bold', 
            color: '#1a1a1a',
            mb: 2,
            fontSize: { xs: '2.5rem', md: '3.5rem' }
          }}>
            Get in Touch
          </Typography>
          <Typography variant="h5" sx={{ 
            color: '#666',
            maxWidth: '800px',
            mx: 'auto',
            fontSize: { xs: '1.1rem', md: '1.3rem' }
          }}>
            Have questions about bookloop? We're here to help! Reach out to us and we'll get back to you as soon as possible.
          </Typography>
        </Box>

        <Grid container spacing={6}>
          {/* Contact Form */}
          <Grid item xs={12} lg={7}>
            <Paper elevation={2} sx={{ p: 4, borderRadius: 3 }}>
              <Typography variant="h4" component="h2" sx={{ 
                fontWeight: 'bold',
                mb: 4,
                color: '#1a1a1a'
              }}>
                Send us a Message
              </Typography>
              
              <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Your Name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            borderColor: '#ddd',
                          },
                          '&:hover fieldset': {
                            borderColor: '#6B89FF',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#6B89FF',
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Email Address"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            borderColor: '#ddd',
                          },
                          '&:hover fieldset': {
                            borderColor: '#6B89FF',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#6B89FF',
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      required
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            borderColor: '#ddd',
                          },
                          '&:hover fieldset': {
                            borderColor: '#6B89FF',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#6B89FF',
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      multiline
                      rows={6}
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            borderColor: '#ddd',
                          },
                          '&:hover fieldset': {
                            borderColor: '#6B89FF',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#6B89FF',
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      sx={{
                        backgroundColor: '#6B89FF',
                        color: 'white',
                        py: 2,
                        fontSize: '16px',
                        fontWeight: 'bold',
                        borderRadius: '14px',
                        '&:hover': {
                          backgroundColor: '#5A75E6',
                        },
                      }}
                    >
                      Send Message
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </Paper>
          </Grid>

          {/* Contact Information */}
          <Grid item xs={12} lg={5}>
            <Box sx={{ mb: 4 }}>
              <Typography variant="h4" component="h2" sx={{ 
                fontWeight: 'bold',
                mb: 3,
                color: '#1a1a1a'
              }}>
                Contact Information
              </Typography>
            </Box>
            
            <Grid container spacing={3}>
              {contactInfo.map((info, index) => (
                <Grid item xs={12} sm={6} lg={12} key={index}>
                  <Paper elevation={2} sx={{ 
                    p: 3, 
                    height: '100%', 
                    display: 'flex', 
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center',
                    transition: 'transform 0.2s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                    },
                  }}>
                    {info.icon}
                    <Typography variant="h6" component="h3" sx={{ 
                      fontWeight: 'bold',
                      mt: 2,
                      mb: 1,
                      color: '#1a1a1a'
                    }}>
                      {info.title}
                    </Typography>
                    <Typography variant="body1" sx={{ 
                      fontWeight: 'bold',
                      color: '#6B89FF',
                      mb: 1
                    }}>
                      {info.content}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#666' }}>
                      {info.description}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>

        {/* FAQ Section */}
        <Box sx={{ mt: 8 }}>
          <Typography variant="h3" component="h2" sx={{ 
            textAlign: 'center',
            fontWeight: 'bold',
            mb: 6,
            color: '#1a1a1a'
          }}>
            Frequently Asked Questions
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
                <Typography variant="h6" component="h3" sx={{ 
                  fontWeight: 'bold',
                  mb: 2,
                  color: '#1a1a1a'
                }}>
                  How do I sell my textbooks?
                </Typography>
                <Typography variant="body1" sx={{ color: '#666' }}>
                  Simply create an account, take photos of your books, set your price, and list them. When someone purchases your book, payment is processed securely through Stripe and you'll receive the funds directly.
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
                <Typography variant="h6" component="h3" sx={{ 
                  fontWeight: 'bold',
                  mb: 2,
                  color: '#1a1a1a'
                }}>
                  Is bookloop safe to use?
                </Typography>
                <Typography variant="body1" sx={{ color: '#666' }}>
                  Yes! All users are verified with their school email address, and we recommend meeting in public places on campus for exchanges.
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
                <Typography variant="h6" component="h3" sx={{ 
                  fontWeight: 'bold',
                  mb: 2,
                  color: '#1a1a1a'
                }}>
                  What payment methods are accepted?
                </Typography>
                <Typography variant="body1" sx={{ color: '#666' }}>
                  We use Stripe for secure online payments. All major credit cards, debit cards, and digital wallets are accepted for safe and convenient transactions.
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
                <Typography variant="h6" component="h3" sx={{ 
                  fontWeight: 'bold',
                  mb: 2,
                  color: '#1a1a1a'
                }}>
                  How do I report an issue?
                </Typography>
                <Typography variant="body1" sx={{ color: '#666' }}>
                  If you encounter any problems, please contact us through this form or email us directly at support@bookloop.com.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </Container>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
} 